import { useRef, useEffect } from "react"
import { getSegmentDeviceId } from "@mindbodygreen/analytics-tracking-helpers"
import useTracking from "hooks/useTracking"
import useProgress from "./useProgress"

/**
 * Trigger track event depending on current articles' progress.
 */
const usePageProgressTracker = (article, index) => {
  const { track } = useTracking()
  const triggerRef = useRef({ 0: false, 25: false, 50: false, 75: false, 100: false })
  const lastTrackedProgress = useRef(0)
  const pageTracked = useRef(false)

  useEffect(() => {
    triggerRef.current = { 0: false, 25: false, 50: false, 75: false, 100: false }
    lastTrackedProgress.current = 0
    pageTracked.current = false
  }, [article.id])

  const trackPageView = () => {
    const { id: articleId, url, titles } = article

    if (typeof window !== "undefined" && window.analytics) {
      const page = `${process.env.GATSBY_LOCAL_SITE}${url}`

      const properties = {
        url: page,
        path: url,
        title: titles?.site || "",
        object_type: "article",
        object_id: articleId,
      }

      const event = {
        page: {
          url: page,
        },
        device: { id: getSegmentDeviceId() },
      }

      // Track page view in Segment
      window.analytics.page(properties, event)

      // Push to dataLayer for GA tracking
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "virtualPageview",
          virtualPageURL: url,
          virtualPageTitle: titles?.site || "",
        })
      }
    }
  }

  const trackProgressEvent = (percentage) => {
    const { id: articleId, authors, tags, category, subcategory } = article

    if (!triggerRef.current[percentage]) {
      track(`Article Viewed ${percentage}%`, {
        label: `${percentage}%`,
        category: "Content Engagement",
        page_type: "Article",
        percent_complete: percentage,
        article_id: articleId,
        article_position: index + 1,
        tags,
        article_category: category,
        article_subcategory: subcategory,
        author: authors && authors.length ? authors[0].name : "",
      })
      triggerRef.current[percentage] = true
      lastTrackedProgress.current = percentage
    }
  }

  const handleScroll = (currentProgress) => {
    // Calculate if scrolling down by comparing with last tracked progress
    const isScrollingDown = currentProgress > lastTrackedProgress.current

    // Only proceed if we're scrolling down
    if (!isScrollingDown) return

    const progress = Math.floor(currentProgress)

    // For first article (index === 0), only track progress
    if (index === 0) {
      if (progress >= 0 && !triggerRef.current[0]) trackProgressEvent(0)
      if (progress >= 25 && !triggerRef.current[25]) trackProgressEvent(25)
      if (progress >= 50 && !triggerRef.current[50]) trackProgressEvent(50)
      if (progress >= 75 && !triggerRef.current[75]) trackProgressEvent(75)
      if (progress >= 100 && !triggerRef.current[100]) trackProgressEvent(100)
      return
    }

    // For subsequent articles, track page view first when they start being viewed
    if (!pageTracked.current && progress > 0) {
      trackPageView()
      pageTracked.current = true
      // After page view is tracked, immediately track 0% progress
      setTimeout(() => {
        if (!triggerRef.current[0]) {
          trackProgressEvent(0)
        }
      }, 100)
      return
    }

    // Continue with other progress events only after page view and 0% are tracked
    if (pageTracked.current && triggerRef.current[0]) {
      if (progress >= 25 && !triggerRef.current[25]) trackProgressEvent(25)
      if (progress >= 50 && !triggerRef.current[50]) trackProgressEvent(50)
      if (progress >= 75 && !triggerRef.current[75]) trackProgressEvent(75)
      if (progress >= 100 && !triggerRef.current[100]) trackProgressEvent(100)
    }
  }

  useProgress(handleScroll, article.id)
}

export default usePageProgressTracker
